import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ExpenseClaim } from "@/types/payroll";
import { Receipt, Plus, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ExpenseClaims() {
  const [claims, setClaims] = useKV<ExpenseClaim[]>("payroll-expenses", [
    {
      id: "exp-1",
      employeeId: "CURRENT_USER",
      employeeName: "Me",
      category: "Travel",
      amount: 150.50,
      currency: "USD",
      date: "2026-01-10",
      description: "Flight to conference",
      status: "Submitted",
      submittedAt: "2026-01-11"
    }
  ]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newClaim, setNewClaim] = useState<Partial<ExpenseClaim>>({ currency: 'USD', category: 'Other' });

  const handleSubmit = () => {
    if (!newClaim.amount || !newClaim.description) {
      toast.error("Please fill in required fields");
      return;
    }

    const claim: ExpenseClaim = {
      id: crypto.randomUUID(),
      employeeId: "CURRENT_USER",
      employeeName: "Me",
      category: newClaim.category as any,
      amount: Number(newClaim.amount),
      currency: newClaim.currency || "USD",
      date: new Date().toISOString(),
      description: newClaim.description,
      status: "Submitted",
      submittedAt: new Date().toISOString()
    };

    setClaims([...claims, claim]);
    setIsDialogOpen(false);
    setNewClaim({ currency: 'USD', category: 'Other' });
    toast.success("Expense claim submitted");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">My Expenses</h3>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Claim
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {claims.map((claim) => (
                <TableRow key={claim.id}>
                  <TableCell>{new Date(claim.date).toLocaleDateString()}</TableCell>
                  <TableCell className="font-medium">{claim.description}</TableCell>
                  <TableCell>{claim.category}</TableCell>
                  <TableCell>{claim.currency} {claim.amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      claim.status === 'Approved' ? 'bg-green-100 text-green-700' : 
                      claim.status === 'Rejected' ? 'bg-red-100 text-red-700' : 
                      'bg-blue-100 text-blue-700'
                    }>
                      {claim.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {claim.status === 'Draft' && (
                      <Button variant="ghost" size="sm">Edit</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {claims.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No expense claims found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Expense Claim</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input 
                  type="number" 
                  placeholder="0.00" 
                  value={newClaim.amount || ''}
                  onChange={(e) => setNewClaim({...newClaim, amount: Number(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={newClaim.currency} onValueChange={(v) => setNewClaim({...newClaim, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={newClaim.category} onValueChange={(v) => setNewClaim({...newClaim, category: v as any})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Travel">Travel</SelectItem>
                  <SelectItem value="Meals">Meals</SelectItem>
                  <SelectItem value="Software">Software</SelectItem>
                  <SelectItem value="Equipment">Equipment</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                placeholder="What is this for?" 
                value={newClaim.description || ''}
                onChange={(e) => setNewClaim({...newClaim, description: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Receipt</Label>
              <Input type="file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>Submit Claim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
